import React from "react";

import { MarkChatUnreadRounded, NotificationsActiveRounded } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import { httpsCallable } from "firebase/functions";

import useAuth from "../../config/context/authContext";
import useModal from "../../config/context/modalContext";

import useFirebase from "../../config/context/firebaseContext";
import useNotification from "../../config/context/notificationContext";
import storageKey from "../../config/storageKey";
import { Dev } from "../../config/util/consoleUtil";

import SwitchMain, { SwitchMainSkeleton } from "../../components/InputMain/SwitchMain";
import MainBorderButton from "../../components/MainBorderButton";
import { TopicSubscriptionService } from "../../config/services/TopicSubscriptionService";
import { TopicMappingType } from "../../types/common";

type MainNotificationManagerProps = {
  topicsToManage: TopicMappingType[];
};

export default function MainNotificationManager({ topicsToManage }: MainNotificationManagerProps) {
  const { currentUserInfo } = useAuth();
  const { requestNotificationPermission, notificationIsActive } = useNotification();

  const { functions } = useFirebase();
  const { pushAlert } = useModal();

  const TopicSubscriptionOne = TopicSubscriptionService.One();

  const [loadingUpdate, setLoadingUpdate] = React.useState(false);

  const [myTopics, setMyTopics] = React.useState<string[]>([]);

  const handleSearchTopics = async () => {
    try {
      const fcmToken = window.localStorage.getItem(storageKey.FCM_TOKEN);

      if (!fcmToken) {
        pushAlert({
          type: "error",
          message:
            "Suas notificações estão ativas mas nenhum token de notificações foi encontrado localmente. Feche e abra novamente o aplicativo, caso o problema persista, contate a gente.",
        });

        return;
      }

      const tokenId = fcmToken.split(":")[1].slice(0, 31);

      TopicSubscriptionOne.runRequest(tokenId);
    } catch (error: any) {
      console.log(...new Dev("handleSend", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter tópicos",
      });
    }
  };

  const updateTopic = async (topic: string) => {
    try {
      setLoadingUpdate(true);
      const registerToNotificationTopic = httpsCallable(functions, "registerToNotificationTopic");

      const result = await registerToNotificationTopic({
        topic,
        fcmToken: currentUserInfo?.fcmToken || window.localStorage.getItem(storageKey.FCM_TOKEN),
      });

      const updatedTopics = (result as any).data.topics;

      setMyTopics(updatedTopics);

      pushAlert({
        type: "success",
        message: updatedTopics?.includes(topic) ? "Registrado" : "Registro removido",
      });
    } catch (error: any) {
      console.log(...new Dev("handleSend", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao alterar registro no tópico",
      });
    } finally {
      setLoadingUpdate(false);
    }
  };

  React.useEffect(() => {
    if (notificationIsActive) handleSearchTopics();
  }, []);

  React.useEffect(() => {
    if (TopicSubscriptionOne?.item?.topics) setMyTopics(TopicSubscriptionOne.item.topics);
  }, [TopicSubscriptionOne.item]);

  return (
    <>
      {notificationIsActive ? (
        <Box>
          <Chip icon={<NotificationsActiveRounded />} label={"Notificações Ativas"} />

          <Box mt={2}>
            {topicsToManage.map((topic) =>
              TopicSubscriptionOne.isLoading ? (
                <SwitchMainSkeleton key={topic.topic} />
              ) : (
                <SwitchMain
                  key={topic.topic}
                  disabled={TopicSubscriptionOne.isLoading || loadingUpdate}
                  name={topic.name}
                  checked={myTopics.includes(topic.topic)}
                  onChange={(checked) => updateTopic(topic.topic)}
                />
              )
            )}
          </Box>
        </Box>
      ) : "Notification" in window ? (
        <Box my={2}>
          <MainBorderButton
            color="black"
            text="Ativar Notificações"
            icon={<MarkChatUnreadRounded />}
            onClick={requestNotificationPermission}
          />
        </Box>
      ) : null}
    </>
  );
}
